import React, { useState } from 'react'
import { Dialog, DialogContent, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import toast from 'react-hot-toast'
import axiosInstant from '../../api'
import { rmnProposalConvertEndpoint } from '../../api/endpoints'
import type { RmnProposalRow } from './RmnProposalsPage'

// RMN variant of the internal ConvertProposalModal: RMN proposals always
// carry their in-network brand, so there is no brand picker — the API
// converts against the proposal's own brand. No product is collected either;
// projects convert with whatever (possibly no) product the wizard attached
// and products get assigned later through the normal project workflow.
// Available to every rmn-authorized user (internal with access and brand
// roles alike).
interface RmnConvertProposalModalProps {
  proposal: RmnProposalRow
  onClose: () => void
  onConverted: () => void
}

export function RmnConvertProposalModal({
  proposal,
  onClose,
  onConverted,
}: RmnConvertProposalModalProps) {
  const [submitting, setSubmitting] = useState(false)
  const projectCount = proposal.project_count

  const convert = async () => {
    if (submitting) return
    setSubmitting(true)
    try {
      await axiosInstant.post(rmnProposalConvertEndpoint(proposal.id), {})
      toast.success('Strategy converted to a live order — TDC production takes it from here.')
      onConverted()
    } catch (err: any) {
      toast.error('Failed to convert the strategy. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <IconButton
        onClick={onClose}
        sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}
        aria-label="Close"
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ p: { xs: 2.5, sm: 4 } }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', mb: 0.5 }}>
          Convert to live order
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: 'var(--text-2)', mb: 3, lineHeight: 1.6 }}>
          Confirms this strategy for <strong>{proposal.brand?.name || 'the brand'}</strong>. All{' '}
          {projectCount} draft project{projectCount === 1 ? '' : 's'} move to TDC production and the
          strategy locks for editing. Products can be assigned to the projects later.
        </Typography>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 28 }}>
          <button
            type="button"
            onClick={onClose}
            className="text-[13.5px] font-semibold text-text-2 border border-border-token rounded-[10px] px-5 py-2.5 bg-transparent cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={convert}
            className={`text-[13.5px] font-semibold text-white rounded-[10px] px-5 py-2.5 border-0 ${
              !submitting ? 'bg-accent cursor-pointer' : 'bg-muted cursor-default'
            }`}
          >
            {submitting ? 'Converting…' : 'Convert to order →'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
